import {
  DEFAULT_REAPPRO_STORE_CATEGORY_PARAMS,
  getErrorMessageFromPossibleBlob,
  updateReapproCategoryAlgorithms,
  useDeliveryRecapExport,
  useProducts,
  useReapproAlgorithmMatrixForm,
  useReapproCategories,
  useReapproCategoryAlgorithms,
  useReapproSimulationExport,
  useStores,
} from '@gozoki/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronLeft, FileSpreadsheet, Store, Tag, Truck } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ReapproConfigAccordion } from '~/components/routes/categories/ReapproConfigAccordion'
import { CategoryConsolidatedPanel } from '~/components/routes/categories/CategoryConsolidatedPanel'
import {
  buildAlgorithmPayload,
  buildParamsStateFromAlgorithms,
  getAlgorithmKey,
  type AlgorithmPair,
} from '~/components/routes/categories/buildAlgorithmPayload'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { Button } from '~/components/ui/button'
import { downloadBlob } from '../../../utils/excel/downloadBlob'

import { withResourcePermission } from '../../../components/RequireResourcePermission'

type ReapproConfigAxis = 'store' | 'category'

export const Route = createFileRoute('/_authenticated/categories/reappro/config')({
  component: withResourcePermission('categories', 'read')(AlgorithmConfigPage),
})

const slugifyStoreLabel = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-+|-+$/g, '')

const getStoreSimulationFilename = (
  store?: {
    id: number
    label: string
    address?: { postalCode?: string }
  } | null,
) => {
  if (!store) {
    return 'reappro-simulation.xlsx'
  }

  const cp = (store.address?.postalCode ?? '').trim().slice(0, 2).toLowerCase()
  const labelSlug = slugifyStoreLabel(store.label)
  const segment = [cp, labelSlug].filter((part) => part.length > 0).join('-')

  if (segment.length === 0) {
    return `reappro-simulation-store-${store.id}.xlsx`
  }

  return `reappro-simulation-${segment}.xlsx`
}

function AlgorithmConfigPage() {
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()

  const { data: allStores = [] } = useStores()
  const boutiques = useMemo(
    () => allStores.filter((store) => store.storeType === 'BOUTIQUE'),
    [allStores],
  )

  const { data: reapproCategories = [], isLoading: isCategoriesLoading } = useReapproCategories()
  const { data: products = [], isLoading: isProductsLoading } = useProducts()
  const { data: algorithms = [], isLoading: isAlgorithmsLoading } = useReapproCategoryAlgorithms()
  const sortedReapproCategories = useMemo(
    () =>
      [...reapproCategories].sort((a, b) =>
        a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }),
      ),
    [reapproCategories],
  )

  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null)

  useEffect(() => {
    if (boutiques.length > 0 && selectedStoreId === null) {
      setSelectedStoreId(boutiques[0].id)
    }
  }, [boutiques, selectedStoreId])

  const selectedStore = boutiques.find((b) => b.id === selectedStoreId)

  const [axis, setAxis] = useState<ReapproConfigAxis>('store')
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)

  useEffect(() => {
    if (sortedReapproCategories.length > 0 && selectedCategoryId === null) {
      setSelectedCategoryId(sortedReapproCategories[0].id)
    }
  }, [sortedReapproCategories, selectedCategoryId])

  const selectedCategory = sortedReapproCategories.find((c) => c.id === selectedCategoryId)

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty, dirtyFields },
  } = useReapproAlgorithmMatrixForm()

  const isLoading = isCategoriesLoading || isAlgorithmsLoading || isProductsLoading

  useEffect(() => {
    if (isLoading) return
    reset({ params: buildParamsStateFromAlgorithms(algorithms) }, { keepDirty: false })
  }, [algorithms, isLoading, reset])

  const handleReset = () =>
    reset({ params: buildParamsStateFromAlgorithms(algorithms) }, { keepDirty: false })

  const updateAlgorithmsMut = useMutation({
    mutationFn: updateReapproCategoryAlgorithms,
    onSuccess: () => {
      toast.success('Algorithmes de réappro mis à jour')
      queryClient.invalidateQueries({ queryKey: ['reapproCategoryAlgorithms'] })
      reset(getValues(), { keepDirty: false })
    },
    onError: () => {
      toast.error("Impossible d'enregistrer les algorithmes de réappro")
    },
  })
  const { mutate: exportReapproSimulation, isPending: isExportingReapproSimulation } =
    useReapproSimulationExport()
  const { mutate: exportDeliveryRecap, isPending: isExportingDeliveryRecap } =
    useDeliveryRecapExport()

  const handleSave = handleSubmit(() => {
    let pairs: AlgorithmPair[] = []
    if (axis === 'store') {
      if (selectedStoreId === null) return
      pairs = sortedReapproCategories.map((category) => ({
        reapproCategoryId: category.id,
        storeId: selectedStoreId,
      }))
    } else {
      if (selectedCategoryId === null) return
      // Blast-radius : n'écrire que les couples déjà sauvés OU édités cette session
      // (un bump marque tous les magasins dirty → flux bump inchangé ; un couple déjà
      // sauvé est toujours ré-inclus → aucune perte de config existante).
      const dirtyKeys = new Set(Object.keys(dirtyFields.params ?? {}))
      const savedKeys = new Set(
        algorithms
          .filter((a) => a.reapproCategoryId === selectedCategoryId)
          .map((a) => getAlgorithmKey(a.reapproCategoryId, a.storeId)),
      )
      pairs = boutiques
        .filter((store) => {
          const key = getAlgorithmKey(selectedCategoryId, store.id)
          return savedKeys.has(key) || dirtyKeys.has(key)
        })
        .map((store) => ({ reapproCategoryId: selectedCategoryId, storeId: store.id }))
    }
    updateAlgorithmsMut.mutate(
      buildAlgorithmPayload(pairs, getValues, DEFAULT_REAPPRO_STORE_CATEGORY_PARAMS),
    )
  })

  const totalProducts = useMemo(() => products.filter((p) => p.isActive).length, [products])

  const categoryProductCount = useMemo(
    () =>
      selectedCategory
        ? products.filter((p) => p.isActive && selectedCategory.products?.includes(p.id)).length
        : 0,
    [products, selectedCategory],
  )

  const handleExportDeliveryRecap = () => {
    exportDeliveryRecap(undefined, {
      onError: async (error) => {
        const errorMessage = await getErrorMessageFromPossibleBlob(error)
        toast.error('Impossible de générer le récap livraison', { description: errorMessage })
      },
      onSuccess: (blob) => {
        downloadBlob({ blob, filename: 'recap-livraison-demain.xlsx' })
        toast.success('Récap livraison généré', {
          description: 'Tout ce qui sera livré demain.',
        })
      },
    })
  }

  const handleExportSimulationXlsx = () => {
    if (!selectedStoreId) {
      toast.error('Aucune boutique sélectionnée')
      return
    }

    exportReapproSimulation(selectedStoreId, {
      onError: async (error) => {
        const errorMessage = await getErrorMessageFromPossibleBlob(error)
        toast.error('Impossible de générer la simulation Excel', {
          description: errorMessage,
        })
      },
      onSuccess: (blob) => {
        downloadBlob({
          blob,
          filename: getStoreSimulationFilename(selectedStore),
        })
        toast.success('Export généré', {
          description: `Simulation complète — ${selectedStore?.label ?? ''} — ${totalProducts} produits exportés.`,
        })
      },
    })
  }

  let categoriesContent = null
  if (isLoading) {
    categoriesContent = (
      <div className="text-center py-12 text-sm text-muted-foreground">Chargement...</div>
    )
  } else if (axis === 'store' && selectedStoreId !== null) {
    categoriesContent = sortedReapproCategories.map((category) => (
      <ReapproConfigAccordion
        key={`${category.id}-${selectedStoreId}`}
        category={category}
        storeId={selectedStoreId}
        title={category.name}
        control={control}
      />
    ))
  } else if (axis === 'category' && selectedCategory) {
    categoriesContent = boutiques.map((store) => (
      <ReapproConfigAccordion
        key={`${selectedCategory.id}-${store.id}`}
        category={selectedCategory}
        storeId={store.id}
        title={store.label}
        control={control}
      />
    ))
  }

  return (
    <>
      {/* Sticky top bar */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate({ to: '/categories', search: { tab: 'reappro' } })}
          >
            <ChevronLeft />
          </Button>
          <div>
            <h1 className="text-lg">Configuration des algorithmes</h1>
            {axis === 'store' && selectedStore && (
              <p className="text-sm text-muted-foreground">
                Gérez les règles de réapprovisionnement pour{' '}
                <span className="text-foreground font-medium">{selectedStore.label}</span>
              </p>
            )}
            {axis === 'category' && selectedCategory && (
              <p className="text-sm text-muted-foreground">
                Gérez les règles de réapprovisionnement pour la catégorie{' '}
                <span className="text-foreground font-medium">{selectedCategory.name}</span> sur
                toutes les boutiques
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <AppTabs<ReapproConfigAxis>
            value={axis}
            onTabChange={setAxis}
            tabs={[
              { value: 'store', label: 'Par magasin', icon: Store },
              { value: 'category', label: 'Par catégorie', icon: Tag },
            ]}
          />
          <div className="flex items-center gap-2 border border-border rounded-lg px-3 py-2">
            {axis === 'store' ? (
              <>
                <Store size={14} className="text-primary shrink-0" />
                <select
                  value={selectedStoreId ?? ''}
                  onChange={(e) => setSelectedStoreId(Number(e.target.value))}
                  className="text-sm bg-transparent focus:outline-none min-w-50 cursor-pointer"
                >
                  {boutiques.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <Tag size={14} className="text-primary shrink-0" />
                <select
                  value={selectedCategoryId ?? ''}
                  onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                  className="text-sm bg-transparent focus:outline-none min-w-50 cursor-pointer"
                >
                  {sortedReapproCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
          {isDirty && (
            <>
              <Button type="button" variant="outline" onClick={handleReset}>
                Réinitialiser
              </Button>
              <Button
                type="submit"
                form="algorithm-config-form"
                disabled={updateAlgorithmsMut.isPending}
              >
                Enregistrer
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Page content */}
      <form id="algorithm-config-form" onSubmit={handleSave}>
        <div className="bg-muted/20 min-h-screen">
          <div className="max-w-300 mx-auto p-6 space-y-4">
            {/* Summary bar */}
            <div className="flex items-center justify-between bg-background rounded-xl border border-border px-5 py-3 shadow-sm">
              <div className="flex items-center gap-3">
                {axis === 'store' ? (
                  <>
                    <Store size={16} className="text-primary" />
                    <span className="text-sm">{selectedStore?.label}</span>
                    <span className="text-xs text-muted-foreground">
                      — {sortedReapproCategories.length} catégories de réapprovisionnement
                    </span>
                  </>
                ) : (
                  <>
                    <Tag size={16} className="text-primary" />
                    <span className="text-sm">{selectedCategory?.name}</span>
                    <span className="text-xs text-muted-foreground">
                      — {boutiques.length} boutiques
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleExportDeliveryRecap}
                  disabled={isExportingDeliveryRecap}
                >
                  <Truck />
                  {isExportingDeliveryRecap ? 'Génération...' : 'Récap livraison demain'}
                </Button>
                {axis === 'store' ? (
                  <>
                    <span className="text-xs text-muted-foreground">{totalProducts} produits</span>
                    <Button
                      type="button"
                      onClick={handleExportSimulationXlsx}
                      disabled={!selectedStoreId || isExportingReapproSimulation}
                    >
                      <FileSpreadsheet />
                      {isExportingReapproSimulation ? 'Génération...' : 'Simulation Excel'}
                    </Button>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {categoryProductCount} produits
                  </span>
                )}
              </div>
            </div>

            {/* Vue consolidée (mode catégorie) */}
            {axis === 'category' && selectedCategory && (
              <CategoryConsolidatedPanel
                category={selectedCategory}
                boutiques={boutiques}
                products={products}
                control={control}
                savedAlgorithms={algorithms}
              />
            )}

            {/* Category accordions */}
            {categoriesContent}
          </div>
        </div>
      </form>
    </>
  )
}
